import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthCtx = createContext(null);

/** rc_user puede quedar corrupto/incompleto tras un deploy o cambio de sesión. */
function parseUsuarioCache() {
  try {
    const raw = localStorage.getItem('rc_user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u || typeof u !== 'object' || Array.isArray(u)) return null;
    if (!u.email && !u.id && !u._id) return null;
    return u;
  } catch {
    return null;
  }
}

function sanitizeUsuario(u) {
  if (!u || typeof u !== 'object' || Array.isArray(u)) return null;
  if (!u.email && !u.id && !u._id) return null;
  return u;
}

export function AuthProvider({ children }) {
  const [usuario, setUsuario]   = useState(parseUsuarioCache);
  const [cargando, setCargando] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('rc_token');
    console.log('🔍 [Auth] Verificando sesión inicial...', !!token);
    if (!token) { setCargando(false); return; }
    
    api.get('/auth/me')
      .then(r => {
        console.log('✅ [Auth] Sesión recuperada:', r.data.usuario?.email);
        const u = sanitizeUsuario(r.data.usuario);
        if (u) setUsuario(u);
        else {
          localStorage.removeItem('rc_token');
          localStorage.removeItem('rc_user');
          setUsuario(null);
        }
      })
      .catch((err) => { 
        console.error('❌ [Auth] Fallo al validar token:', err.response?.status);
        localStorage.removeItem('rc_token'); 
        localStorage.removeItem('rc_user'); 
        setUsuario(null);
      })
      .finally(() => setCargando(false));
  }, []);

  const login = useCallback(async (email, password) => {
    console.log('🚀 [Auth] Intentando login...', email);
    const { data } = await api.post('/auth/login', { email, password });
    const u = sanitizeUsuario(data.usuario) || { email, nombre: email.split('@')[0] };
    localStorage.setItem('rc_token', data.token);
    localStorage.setItem('rc_user', JSON.stringify(u));
    console.log('✅ [Auth] Login exitoso, guardando usuario:', u.email);
    setUsuario(u);
    return u;
  }, []);

  const registro = useCallback(async (form) => {
    const { data } = await api.post('/auth/registro', form);
    const u = sanitizeUsuario(data.usuario) || { email: form.email, nombre: form.nombre };
    localStorage.setItem('rc_token', data.token);
    localStorage.setItem('rc_user', JSON.stringify(u));
    setUsuario(u);
    return u;
  }, []);

  const updateUsuario = useCallback((nuevoUsuario) => {
    const u = sanitizeUsuario(nuevoUsuario);
    if (!u) return;
    localStorage.setItem('rc_user', JSON.stringify(u));
    setUsuario(u);
  }, []);

  const logout = useCallback(() => {
    console.log('🚪 [Auth] Cerrando sesión...');
    localStorage.removeItem('rc_token');
    localStorage.removeItem('rc_user');
    setUsuario(null);
  }, []);

  const esAdmin   = usuario?.rol === 'admin';
  const esFinanzas = usuario?.rol === 'finanzas' || usuario?.rol === 'admin';
  const esMaestro = usuario?.rol === 'maestro' || usuario?.rol === 'admin';
  const logueado  = !!usuario;

  useEffect(() => {
    if (usuario) console.log('👤 [Auth] Estado de usuario actualizado:', usuario.email, 'Rol:', usuario.rol);
  }, [usuario]);

  return (
    <AuthCtx.Provider value={{ usuario, cargando, login, registro, logout, updateUsuario, esAdmin, esMaestro, esFinanzas, logueado }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
